import { FINDY_REFERRER_TAGS } from '@findy-code/yuffie/models';
import type { ReactNode } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';

import { FindyLogoLink } from '../../../components/findy-logo-link.component';
import { NavExternalLinkItem, NavLinkItem } from '../../../components/nav-item';
import { UserNameDropdownMenu } from '../../components/user-name-dropdown-menu/user-name-dropdown-menu.component';
import { CountBadge, InlineCountBadge } from '../count-badge.component';
import styles from './main-desktop-content.component.module.scss';

type Props = {
  children: ReactNode;
  pathname: string;
  userName: string;
  unreadMessageCount: number;
  unreadCount: number;
  profileCompletionProgress: number;
  isFreelanceUser: boolean;
  isTargetUser: boolean;
  onLogout: () => void;
};

// TODO:
// 設定アイコンにborder-bottomが付与されると、アイコンの位置がズレる
// styleを無理やり調節しているので、適切な修正を行う
export const MainDesktopContent = ({
  children: CareerMotivationButton,
  pathname,
  userName,
  unreadMessageCount,
  unreadCount,
  profileCompletionProgress,
  isFreelanceUser,
  isTargetUser,
  onLogout,
}: Props) => {
  return (
    <div className={styles.privateDesktopContent}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <FindyLogoLink />
        <nav>
          <ul className={styles.menu}>
            <NavLinkItem href="/home" isCurrentPage={pathname === '/home'}>
              マイページ
            </NavLinkItem>
            <NavLinkItem
              href="/recommends"
              isCurrentPage={
                pathname === '/recommends' || pathname === '/companies'
              }
            >
              求人・企業検索
            </NavLinkItem>
            <NavLinkItem
              href="/pick-up"
              isCurrentPage={pathname.startsWith('/pick-up')}
            >
              メディア
            </NavLinkItem>
            <NavLinkItem
              href="/events"
              isCurrentPage={pathname.startsWith('/events')}
            >
              イベント
            </NavLinkItem>
            <NavLinkItem
              href="/likes"
              isCurrentPage={pathname.startsWith('/likes')}
            >
              いいね一覧
            </NavLinkItem>
            <NavLinkItem
              href="/matches"
              isCurrentPage={pathname.startsWith('/matches')}
            >
              <span
                style={{
                  display: 'inline-flex',
                  gap: '4px',
                  justifyContent: 'center',
                }}
              >
                メッセージ
                {unreadMessageCount > 0 && (
                  <InlineCountBadge count={unreadMessageCount} />
                )}
              </span>
            </NavLinkItem>
            <NavLinkItem
              href="/settings/profile"
              isCurrentPage={/^\/settings\/(account|profile|personality|desired_condition|resume)/i.test(
                pathname
              )}
            >
              プロフィール
              {profileCompletionProgress !== 100 && (
                <span
                  aria-label="プロフィールに未入力項目があります"
                  className={styles.profileIncompletionMark}
                />
              )}
            </NavLinkItem>
            {isFreelanceUser && (
              <NavExternalLinkItem
                href={`https://freelance.findy-code.io/?utm_source=fc_mypage&fr=${FINDY_REFERRER_TAGS.FINDY_FREELANCE_REFERRAL}`}
                rel="noopener"
                target="_blank"
              >
                フリーランス案件
                <FaExternalLinkAlt
                  aria-hidden="true"
                  size="10px"
                  style={{ verticalAlign: 'top', marginLeft: '2px' }}
                />
              </NavExternalLinkItem>
            )}
            {!isFreelanceUser && isTargetUser && (
              <NavLinkItem
                href="/us-support"
                isCurrentPage={pathname.startsWith('/us-support')}
              >
                転職サポート
              </NavLinkItem>
            )}
          </ul>
        </nav>
      </div>
      <ul className={styles.menu} style={{ alignItems: 'center' }}>
        <li>{CareerMotivationButton}</li>
        <NavLinkItem
          href="/notifications"
          isCurrentPage={pathname.startsWith('/notifications')}
          style={{ position: 'relative' }}
        >
          <svg
            aria-label="お知らせ"
            fill="currentColor"
            height="20px"
            stroke="currentColor"
            style={
              pathname.startsWith('/notifications')
                ? { marginBottom: '-7px' }
                : { marginBottom: '-5px' }
            }
            viewBox="0 0 448 512"
            width="20px"
          >
            <path d="M224 512c35.32 0 63.97-28.65 63.97-64H160.03c0 35.35 28.65 64 63.97 64zm215.39-149.71c-19.32-20.76-55.47-51.99-55.47-154.29 0-77.7-54.48-139.9-127.94-155.16V32c0-17.67-14.32-32-31.98-32s-31.98 14.33-31.98 32v20.84C118.56 68.1 64.08 130.3 64.08 208c0 102.3-36.15 133.53-55.47 154.29-6 6.45-8.66 14.16-8.61 21.71.11 16.4 12.98 32 32.1 32h383.8c19.12 0 32-15.6 32.1-32 .05-7.55-2.61-15.27-8.61-21.71z"></path>
          </svg>
          {/* TODO: 新たにCountBadgeコンポーネントを作る */}
          {unreadCount > 0 && (
            <CountBadge
              count={unreadCount}
              style={{
                top: `${pathname.startsWith('/notifications') ? '6px' : '4px'}`,
                right: '4px',
                display: 'inline-block',
                minWidth: '10px',
                height: `${
                  pathname.startsWith('/notifications') ? '16.6px' : '17.6px'
                }`,
                padding: '4px 5px',
                marginLeft: '.3em',
                fontSize: '.8em',
                textAlign: 'center',
                whiteSpace: 'nowrap',
              }}
            />
          )}
        </NavLinkItem>
        <li>
          <UserNameDropdownMenu
            profileCompletionProgress={profileCompletionProgress}
            userName={userName}
            onLogout={onLogout}
          />
        </li>
      </ul>
    </div>
  );
};
